/**
 * Session-lifetime policy — web client mirror (Task #3061).
 *
 * Mirrors the values in `artifacts/api-server/src/lib/session-policy.ts`
 * so the two sides agree on what "30 days absolute max" / "30 min admin
 * idle" mean. The server is the authoritative gate (it rejects expired
 * sessions even if the client misbehaves); the client uses these only
 * for UX timing (idle-timeout watcher, remember-me default).
 *
 * If the server values are ever overridden via env, the divergence is
 * intentionally tolerated — the server gate will reject before the
 * client's idle watcher cares.
 */

export const ACCESS_TOKEN_TTL_SEC = 60 * 60;
export const REFRESH_SLIDING_TTL_SEC = 14 * 24 * 60 * 60;
export const REFRESH_ABSOLUTE_MAX_SEC = 30 * 24 * 60 * 60;
export const ADMIN_IDLE_TIMEOUT_SEC = 2 * 60 * 60;
export const REMEMBER_ME_DEFAULT = true;

/**
 * localStorage key recording the user's last Remember-me choice. The
 * Supabase client's custom storage adapter (see `supabase/client.ts`)
 * checks this on every write to decide whether to persist tokens to
 * localStorage (survives browser close) or sessionStorage (cleared on
 * close). Storing the *preference* itself in localStorage is fine — it
 * leaks no session material, just a boolean.
 */
export const REMEMBER_ME_STORAGE_KEY = "bac_remember_me";
let unavailableStoragePreference = REMEMBER_ME_DEFAULT;
let preferenceWriteFailed = false;

export function getRememberMePreference(): boolean {
  if (preferenceWriteFailed) return unavailableStoragePreference;
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(REMEMBER_ME_STORAGE_KEY) : null;
    if (raw === null) return REMEMBER_ME_DEFAULT;
    return raw === "1" || raw === "true";
  } catch {
    return unavailableStoragePreference;
  }
}

export function setRememberMePreference(remember: boolean): void {
  unavailableStoragePreference = remember;
  try {
    if (typeof localStorage === "undefined") {
      preferenceWriteFailed = true;
      return;
    }
    localStorage.setItem(REMEMBER_ME_STORAGE_KEY, remember ? "1" : "0");
    preferenceWriteFailed = false;
  } catch {
    preferenceWriteFailed = true;
  }
}
